/**
 * A tiny, non-sensitive note of who was last signed in, so that reloading the
 * installed app while offline keeps the shell instead of showing the sign-in
 * form (`GET /user` cannot answer without a network).
 *
 * It holds the account id, the display name, whether the email was verified
 * and whether the profile is a guest (a guest has no email at all). It never holds a token, the email address, settings, partner data
 * or anything about a game, and it grants nothing: every request still needs
 * the httpOnly session cookie, and the server answers 401 as soon as the
 * browser is back online (which clears this note). It is erased on sign-out,
 * on a 401 and when the account is deleted.
 */
export const LAST_USER_KEY = 'hc_last_user';

export interface LastUserMarker {
  id: number;
  name: string;
  verified: boolean;
  /** A guest profile (`users.is_guest`); absent in markers written before guests existed. */
  guest: boolean;
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function readLastUser(): LastUserMarker | null {
  try {
    const raw = storage()?.getItem(LAST_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LastUserMarker> | null;
    if (
      parsed &&
      typeof parsed.id === 'number' &&
      typeof parsed.name === 'string' &&
      typeof parsed.verified === 'boolean'
    ) {
      return {
        id: parsed.id,
        name: parsed.name,
        verified: parsed.verified,
        guest: parsed.guest === true,
      };
    }
  } catch {
    // Unreadable or blocked storage: nothing is remembered.
  }
  return null;
}

export function writeLastUser(user: {
  id: number;
  name: string;
  email_verified_at?: string | null;
  is_guest?: boolean;
}): void {
  try {
    const marker: LastUserMarker = {
      id: user.id,
      name: user.name,
      verified: Boolean(user.email_verified_at),
      guest: user.is_guest === true,
    };
    storage()?.setItem(LAST_USER_KEY, JSON.stringify(marker));
  } catch {
    // Storage full or blocked: the offline shell is a convenience only.
  }
}

export function clearLastUser(): void {
  try {
    storage()?.removeItem(LAST_USER_KEY);
  } catch {
    // Nothing to clear when storage is blocked.
  }
}
